import React, { CSSProperties, ReactNode, Fragment } from 'react';
import { FormControl, useFormField, FieldProps } from '@core/utils/form';
import { StyledProps, FormHelperText } from '@material-ui/core';
import './form_radio.scss';
interface ContextData<T extends any> {
  props: FormRadioGroupProps<T> | null;
}

const Context = React.createContext<ContextData<any>>({ props: null });

export interface FormRadioGroupProps<V = any> extends FieldProps<FormControl<V>> {
  children?: React.ReactNode;
}

function FormRadioGroupComponent<V = any>(props: FormRadioGroupProps<V>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;

  return (
    <Context.Provider value={{ props }}>
      {!!fieldError && <FormHelperText className="text-danger mb-2">{fieldError}</FormHelperText>}
      {props.children}
    </Context.Provider>
  );
}

export interface FormRadioProps<V = any> {
  value: V;
  label?: React.ReactNode;
  disabled?: boolean;
  onPress?(value: V): void;
  noPadding?: boolean;
  style?: CSSProperties;
  onItemRender?(value: V, isSelected: boolean, onPress: () => void): ReactNode;
  course?: boolean;
}

function FormRadioComponent<V = any>(props: FormRadioProps<V>) {
  const context = React.useContext(Context);
  const { formControl, fieldState, fieldConfig } = useFormField(context && context.props!);
  const { course = false } = props;

  const onPress = React.useCallback(() => {
    formControl.setValue(props.value);
    if (props.onPress) props.onPress(props.value);
  }, [formControl, props]);

  const active = fieldState && fieldState.value === props.value;

  const disabled = fieldConfig.disabled || props.disabled || false;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const error = !!fieldState.errors;

  if (props.onItemRender) {
    return <Fragment>{props.onItemRender(props.value, active, onPress)}</Fragment>;
  }

  if (course) {
    return (
      <div
        className={`d-flex flex-column course-radio-button ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={disabled ? undefined : onPress}
      >
        <span>{props.label}</span>
      </div>
    );
  }
  return (
    <React.Fragment>
      <div className={`custom-control custom-radio custom-radio-primary ${props.noPadding ? '' : 'mb-3'}`} style={props.style}>
        <input
          id={`${formControl.uid}-${props.value}`}
          type="radio"
          name={formControl.uid}
          className="custom-control-input"
          checked={active}
          onChange={onPress}
          onFocus={() => formControl.onFocus()}
          onBlur={() => formControl.onBlur()}
          disabled={disabled}
        />

        <label className="custom-control-label" htmlFor={`${formControl.uid}-${props.value}`}>
          {props.label}
        </label>
      </div>
    </React.Fragment>
  );
}

export class FormRadio<V = any> extends React.PureComponent<FormRadioProps<V>> {
  public render() {
    return <FormRadioComponent {...this.props} />;
  }
}

export class FormRadioGroup<V = any> extends React.PureComponent<FormRadioGroupProps<V>> {
  public render() {
    return <FormRadioGroupComponent {...this.props} />;
  }
}
